/** Filtering and the monthly breakdown of the P-GK invoice list (Faktury P-GK).
 *
 *  Everything here is pure so the page stays a thin renderer: the list endpoint
 *  returns the whole invoice book in one go, so the filter bar and the monthly
 *  table both work off that single response instead of re-querying per filter.
 */
import { displayAmount, netTotals, type InvoiceAmounts } from "./invoiceTotals";

/** The row fields the filter bar and the monthly table read. */
export interface FilterableInvoice extends InvoiceAmounts {
  invoice_number: string;
  order_number: string;
  contractor_name: string;
  submitted_by_name: string;
  document_label?: string;
  project_id?: string | null;
  project_name?: string;
  booking_status: string;
  payment_status: string;
  submitted_at: string;
}

export interface PgkFilters {
  projects: string[];
  contractors: string[];
  documentTypes: string[];
  statuses: string[];
  bookingStatuses: string[];
  paymentStatuses: string[];
  authors: string[];
  /** Free text — matched against the invoice number and the order number. */
  search: string;
  /** Submission date range (P-GK invoices carry no separate issue date). */
  submittedFrom: string;
  submittedTo: string;
}

/** Value standing for "order without a project" in the project filter. */
export const NO_PROJECT = "__no_project__";

export const EMPTY_PGK_FILTERS: PgkFilters = {
  projects: [],
  contractors: [],
  documentTypes: [],
  statuses: [],
  bookingStatuses: [],
  paymentStatuses: [],
  authors: [],
  search: "",
  submittedFrom: "",
  submittedTo: "",
};

// Filters survive F5 / coming back the next day. Keys are per-user: localStorage is
// shared browser-wide and a plain logout does not wipe it, so a fixed key would show
// one user's filters (and the calculator's cached amounts) to whoever logs in next
// on the same machine.
export const pgkFiltersStorageKey = (userId: string | undefined) =>
  `elg5.pgkInvoices.filters.v1.${userId ?? "anon"}`;
export const pgkSheetCalcStorageKey = (userId: string | undefined) =>
  `elg5.pgkInvoices.sheetCalc.v1.${userId ?? "anon"}`;

/** Restores saved filters; first visit opens the full list (no date limit). */
export function loadStoredPgkFilters(storageKey: string): PgkFilters {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return EMPTY_PGK_FILTERS;
    return { ...EMPTY_PGK_FILTERS, ...(JSON.parse(raw) as Partial<PgkFilters>) };
  } catch {
    // Storage unavailable — filters simply won't survive a reload.
    return EMPTY_PGK_FILTERS;
  }
}

export function hasActivePgkFilters(f: PgkFilters): boolean {
  return (
    f.projects.length > 0 ||
    f.contractors.length > 0 ||
    f.documentTypes.length > 0 ||
    f.statuses.length > 0 ||
    f.bookingStatuses.length > 0 ||
    f.paymentStatuses.length > 0 ||
    f.authors.length > 0 ||
    f.search.trim() !== "" ||
    f.submittedFrom !== "" ||
    f.submittedTo !== ""
  );
}

/** Date part of the submission timestamp, as stored (`YYYY-MM-DD`). */
function submittedDay(inv: FilterableInvoice): string {
  return (inv.submitted_at ?? "").slice(0, 10);
}

export function applyPgkFilters<T extends FilterableInvoice>(list: T[], f: PgkFilters): T[] {
  const query = f.search.trim().toLowerCase();
  const has = (sel: string[], value: string) => sel.length === 0 || sel.includes(value);
  return list.filter((inv) => {
    if (!has(f.projects, inv.project_id || NO_PROJECT)) return false;
    if (!has(f.contractors, inv.contractor_name || "")) return false;
    if (!has(f.documentTypes, inv.document_label || "")) return false;
    if (!has(f.statuses, inv.status)) return false;
    if (!has(f.bookingStatuses, inv.booking_status)) return false;
    if (!has(f.paymentStatuses, inv.payment_status)) return false;
    if (!has(f.authors, inv.submitted_by_name || "")) return false;
    if (query) {
      const haystack = `${inv.invoice_number} ${inv.order_number}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    const day = submittedDay(inv);
    if (f.submittedFrom && (!day || day < f.submittedFrom)) return false;
    if (f.submittedTo && (!day || day > f.submittedTo)) return false;
    return true;
  });
}

/** Distinct filter options, ordered for a dropdown (empty values dropped). */
export function distinctOptions<T extends FilterableInvoice>(
  list: T[],
  pick: (inv: T) => string | null | undefined,
  label?: (inv: T) => string,
): { value: string; label: string }[] {
  const byValue = new Map<string, string>();
  for (const inv of list) {
    const value = pick(inv);
    if (!value) continue;
    if (!byValue.has(value)) byValue.set(value, label ? label(inv) : value);
  }
  return [...byValue.entries()]
    .map(([value, text]) => ({ value, label: text }))
    .sort((a, b) => a.label.localeCompare(b.label, "pl"));
}

const MONTHS_PL = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];

export interface PgkMonthRow {
  /** `YYYY-MM` — also the stable id of the row's calculator cells. */
  key: string;
  label: string;
  /** Accepted invoices (net; UZ/UoD by the contract amount). */
  accepted: number;
  /** Submitted, still awaiting the accept/reject decision. */
  pending: number;
  /** Paid part — payments are recorded gross, so the net share is proportional. */
  paid: number;
  outstanding: number;
}

function round2(value: number): number {
  return Number(value.toFixed(2));
}

/**
 * Month-by-month breakdown of whatever list is handed in — the page passes the
 * FILTERED list, so the table answers for the same invoices the user is looking at.
 * Months run from the earliest to the latest submission present, with empty months
 * in between kept so a gap reads as a gap. Rejected and withdrawn invoices are out
 * of the money columns everywhere, exactly like the summary tiles.
 */
export function pgkMonthlyStats(list: FilterableInvoice[]): PgkMonthRow[] {
  const byMonth = new Map<string, FilterableInvoice[]>();
  for (const inv of list) {
    const key = submittedDay(inv).slice(0, 7);
    if (key.length !== 7) continue;
    const bucket = byMonth.get(key);
    if (bucket) bucket.push(inv);
    else byMonth.set(key, [inv]);
  }
  if (byMonth.size === 0) return [];

  const keys = [...byMonth.keys()].sort();
  const [startY, startM] = keys[0].split("-").map(Number);
  const [endY, endM] = keys[keys.length - 1].split("-").map(Number);

  const rows: PgkMonthRow[] = [];
  let y = startY;
  let m = startM;
  while (y < endY || (y === endY && m <= endM)) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    const monthInvs = byMonth.get(key) ?? [];
    const sum = (arr: FilterableInvoice[]) =>
      arr.reduce((acc, inv) => acc + parseFloat(displayAmount(inv)), 0);
    const totals = netTotals(monthInvs);
    rows.push({
      key,
      label: `${MONTHS_PL[m - 1]} ${y}`,
      accepted: round2(sum(monthInvs.filter((inv) => inv.status === "accepted"))),
      pending: round2(sum(monthInvs.filter((inv) => inv.status === "submitted"))),
      paid: round2(totals.paid),
      outstanding: round2(totals.outstanding),
    });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return rows;
}

/** "Razem" row of the monthly table. */
export function pgkMonthlyTotals(rows: PgkMonthRow[]) {
  return {
    accepted: round2(rows.reduce((s, r) => s + r.accepted, 0)),
    pending: round2(rows.reduce((s, r) => s + r.pending, 0)),
    paid: round2(rows.reduce((s, r) => s + r.paid, 0)),
    outstanding: round2(rows.reduce((s, r) => s + r.outstanding, 0)),
  };
}
